import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Link2, Plus } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PinnedItemRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinnedItemRow";
import type { PinKind } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type PinnedItem = {
	id: number;
	title: string;
	canUnpin: boolean;
};

type Props = {
	kind: PinKind;
	open: boolean;
	onClose: () => void;
	pinnedItems: PinnedItem[];
	isPending: boolean;
	onPickExisting: () => void;
	onPickNew?: () => void;
	onUnpin: (id: number) => void;
};

export const PinChoiceDrawer: FC<Props> = ({
	kind,
	open,
	onClose,
	pinnedItems,
	isPending,
	onPickExisting,
	onPickNew,
	onUnpin,
}) => {
	const theme = useTheme();
	const { palette, iconSize, spacingTokens } = theme;
	const { t } = useResource();
	const kindResources = Resources.PortfolioDetail.Pin.Kind[kind];
	const showNewItem = onPickNew !== undefined;

	return (
		<MyPlannDrawer
			anchor="bottom"
			grabber
			open={open}
			onClose={onClose}
			title={t(kindResources.chooseTitle)}
		>
			<Stack pb={spacingTokens.xl}>
				<Typography
					variant="caption"
					color={palette.grey[500]}
					px={spacingTokens.lg}
					pb={1.4}
					display="block"
				>
					{t(kindResources.chooseIntro)}
				</Typography>

				{pinnedItems.length > 0 && (
					<Box pb={1.4}>
						<Typography
							variant="caption"
							color={palette.grey[400]}
							px={spacingTokens.lg}
							pb={spacingTokens.sm}
							textTransform="uppercase"
							display="block"
						>
							{t(Resources.PortfolioDetail.Pin.alreadyPinned)}
						</Typography>
						{pinnedItems.map((item) => (
							<PinnedItemRow
								key={item.id}
								title={item.title}
								canUnpin={item.canUnpin}
								isPending={isPending}
								onUnpin={() => onUnpin(item.id)}
							/>
						))}
					</Box>
				)}

				<ListRow
					showChevron
					onClick={onPickExisting}
					icon={<Link2 size={iconSize.md} color={palette.blue[600]} />}
					iconBg={palette.blue[50]}
					title={t(kindResources.existingTitle)}
					subtitle={t(kindResources.existingSubtitle)}
					isLast={!showNewItem}
				/>

				{showNewItem && (
					<ListRow
						showChevron
						onClick={onPickNew}
						icon={<Plus size={iconSize.md} color={palette.green[600]} />}
						iconBg={palette.green[50]}
						title={t(Resources.PortfolioDetail.Pin.Kind.document.newTitle)}
						subtitle={t(
							Resources.PortfolioDetail.Pin.Kind.document.newSubtitle,
						)}
						isLast
					/>
				)}
			</Stack>
		</MyPlannDrawer>
	);
};
